/**
 * TryonModal — popup live virtual try-on for a single product.
 *
 * Opened from a product's "Try On" button. Shows the live camera with the chosen
 * frame auto-applied, plus recommended similar frames in the strip.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { Product } from '../../types';
import { loadTryonCatalogue } from '../../lib/tryon/catalogue';
import LiveTryonStage from './LiveTryonStage';

interface TryonModalProps {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  /** If the parent already has the catalogue, pass it to skip the fetch. */
  products?: Product[];
}

const TryonModal: React.FC<TryonModalProps> = ({ open, onClose, product, products }) => {
  const navigate = useNavigate();
  const [fetched, setFetched] = useState<Product[]>([]);

  const catalogue = products && products.length > 0 ? products : fetched;

  // Load the shared frame catalogue the first time the modal is opened without
  // one supplied by the parent. `loadTryonCatalogue` memoises across mounts, so
  // opening several cards' modals costs a single network round of paging.
  useEffect(() => {
    if (!open) return;
    if (products && products.length > 0) return;
    let cancelled = false;
    loadTryonCatalogue().then((frames) => {
      if (!cancelled) setFetched(frames);
    });
    return () => { cancelled = true; };
  }, [open, products]);

  // Ensure the target product itself is in the catalogue (it always has an image).
  const fullCatalogue = useMemo(() => {
    if (!product) return catalogue;
    return catalogue.some((p) => p.id === product.id) ? catalogue : [product, ...catalogue];
  }, [catalogue, product]);

  const target = useMemo(
    () => fullCatalogue.find((p) => p.id === product?.id) ?? product ?? null,
    [fullCatalogue, product]
  );

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  // Render through a portal to <body> so the modal is a true full-screen overlay
  // and is never clipped or shrunk by the product card's positioning/overflow.
  return createPortal(
    <div className="tryon-modal-overlay" onClick={onClose}>
      <div className="tryon-modal" onClick={(e) => e.stopPropagation()}>
        <button className="tryon-modal__close" onClick={onClose} aria-label="Close">×</button>
        <LiveTryonStage
          products={fullCatalogue}
          loadingProducts={false}
          targetProduct={target}
          compact
          onViewDetails={(p) => { onClose(); navigate(`/product/${p.id}`); }}
        />
      </div>
    </div>,
    document.body
  );
};

export default TryonModal;
